// The founding-thesis dossier (P1): the trade-offs of each bet AS A 1993
// FOUNDER WOULD WEIGH THEM. The content files know the future — obsolescence
// dates, royalty windows, the cancellation beat — but the player must not:
// strategic interest and standardization risk render as possibilities ("their
// contracts carry a walk-away clause"), never as the schedule. Authored copy
// lives app-side (the ScenarioMeta precedent — content defs stay display-free);
// every number is derived from content, so an unauthored tree still renders
// honest derived-only cards.

import type { ContentPack, PlayerView, TechNodeDef } from "../../engine/types.js";
import { money } from "../format.js";
import { activeTreeOf } from "../screens/shared.js";

export interface ThesisCopy {
  /** What you are betting, one line. */
  bet: string;
  /** How the era reads the bet right now (the pitch framing). */
  window: string;
  /** The failure mode, framed as a risk — never as the date it fires. */
  risk?: string;
}

export const THESIS_COPY: Record<string, ThesisCopy> = {
  "thesis-triangle-raster": {
    bet: "Draw what everyone already draws — triangles — faster than anyone else.",
    window:
      "A crowded deck in '93: every partner on Sand Hill has seen nine rasterizer pitches this quarter, so the premium is small. But it is the one bet the world cannot standardize against.",
  },
  "thesis-quadratic": {
    bet: "Curved surfaces in real time — the demo nobody else can put on a projector.",
    window:
      "The hottest story in the Valley right now, and strategic money is circling. Contrarian architecture pays best before the standards war is settled — and it is not settled.",
    risk:
      "If the industry's APIs settle on triangles, forward-mapped quads become a translation layer nobody wants to write — the bet would discount steeply, fast.",
  },
  "thesis-3d-only": {
    bet: "No 2D, no VGA, no compromises — pure 3D throughput for buyers who care about nothing else.",
    window:
      "Enthusiasts and arcade boards pay for the fastest part in the room, and arcade money arrives early. OEMs won't touch a two-chip solution — the volume lane needs a 2D story you won't have.",
  },
  "thesis-tbdr": {
    bet: "Render only what's visible — a die-cost curve the brute-force crowd can't follow.",
    window:
      "Pitches poorly to generalist VCs: the virtue is a cost curve, invisible on a projector. Console-strategic buyers understand it. The hardest research on this board — outcomes swing wider.",
  },
};

/** Undated obsolescence framing for any node carrying a rider that hasn't
 *  fired yet — authored thesis copy first, a generic read otherwise. */
export function riskLine(node: TechNodeDef): string | null {
  const authored = THESIS_COPY[node.id]?.risk;
  if (authored) return authored;
  if (node.obsolescence) {
    return "standardization risk: the world can settle on a rival approach — if it does, this work discounts steeply overnight.";
  }
  return null;
}

/** Dossier body shared by the GARAGE bet cards and the TREE tier-0 cards. */
export function ThesisNotes({
  node,
  view,
  content,
  showWindow,
}: {
  node: TechNodeDef;
  view: PlayerView;
  content: ContentPack;
  showWindow: boolean;
}) {
  const m = view.monthIndex;
  const copy = THESIS_COPY[node.id];
  const tree = activeTreeOf(content);
  const nameOf = (id: string) => tree?.nodes.find((x) => x.id === id)?.displayName ?? id;
  // strategic principals court thesis holders only while there's still no
  // company — the P0-lite contract signs (or doesn't) with the seed
  const dc =
    view.stage === "garage"
      ? content.scenario.devContracts?.find((d) => d.thesisId === node.id && d.signByMonth >= m)
      : undefined;
  const risk = riskLine(node);
  return (
    <>
      {copy && <div style={{ fontSize: 12, marginBottom: 4 }}>{copy.bet}</div>}
      {showWindow && copy && (
        <div className="hint" style={{ marginBottom: 4 }}>
          {copy.window}
        </div>
      )}
      {dc && (
        <div style={{ color: "var(--cyan)", fontSize: 11, marginBottom: 4 }}>
          STRATEGIC INTEREST: {dc.principal} is circling — hold this thesis at incorporation and expect ~{money(dc.nreMonthly)}/mo of NRE
          {dc.royaltyMonthly > 0 ? ", plus committed socket royalties if their box ships" : " (study money; no socket committed)"}, in
          exchange for their feature list on your first chip.
          {dc.cancelFee ? " Console programs die young — theirs would carry a cancellation clause with a walk-away fee." : ""}
        </div>
      )}
      {risk && (
        <div className="warn-text" style={{ fontSize: 11, marginBottom: 4 }}>
          {risk}
        </div>
      )}
      {(node.creditsTo?.length ?? 0) > 0 && (
        <div className="hint" style={{ marginBottom: 4 }}>
          if the bet goes wrong: {node.creditsTo!.map((c) => `~${Math.round(c.frac * 100)}% of the work carries to ${nameOf(c.id)}`).join(", ")}
        </div>
      )}
    </>
  );
}
